import { notify } from "@general-backoffice/core/index";
import { useCallback, useRef } from "react";

import {
  type BotConfig,
  type BotConfigCreateInput,
  botConfigsEndpoints,
  type BotConfigUpdateInput,
  UserConfigSchema,
} from "../../../api";
import { type StrapiId, useStrapiOne } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import handleError from "../../utils/handleError";

interface UseBotProps {
  [key: string]: unknown;
}

const useBot = (id?: StrapiId, props?: UseBotProps) => {
  // Store options ref for the current update call
  const submitOptionsRef = useRef<SubmitBotOptions | undefined>(undefined);

  const {
    item: bot,
    setItem: setBot,
    isLoading,
    setLoading,
    update: baseUpdate,
    ...other
  } = useStrapiOne({
    id,
    ...props,
    loadItem: loadBot,
    submitItem: (bot) => submitBot(bot, submitOptionsRef.current),
  });

  const update = useCallback(
    (newItem?: BotConfig, options?: SubmitBotOptions) => {
      submitOptionsRef.current = options;
      return baseUpdate(newItem ?? undefined);
    },
    [baseUpdate]
  );

  return {
    bot,
    setBot,
    isLoading,
    setLoading,
    update,
    ...other,
  };
};

export const loadBot = async (id: StrapiId, params?: Record<string, unknown>): Promise<BotConfig | undefined> => {
  try {
    const numericId = typeof id === "string" ? parseInt(id, 10) : id;
    const { data } = await botConfigsEndpoints.getBotConfig(numericId, params);
    return data;
  } catch (e: unknown) {
    handleError(getErrorMessage(e, "Error while loading bots"));
  }
};

interface SubmitBotOptions {
  paymentMethodId?: number | string;
}

export const submitBot = async (
  bot: Partial<BotConfig>,
  options?: SubmitBotOptions
): Promise<BotConfig | undefined> => {
  const isNew = !bot?.id;
  const errMsg = `Bot ${isNew ? "creation" : "update"} failed`;
  if (!bot) throw new Error(errMsg);

  try {
    const { id, disabled, userConfig, strategy, exchange_account } = bot;

    if (isNew) {
      // Create requires exchange_account and strategy
      const rawExchangeAccountId =
        typeof exchange_account === "object" && exchange_account !== null && "id" in exchange_account
          ? exchange_account.id
          : undefined;
      const exchangeAccountId =
        typeof rawExchangeAccountId === "number" || typeof rawExchangeAccountId === "string"
          ? rawExchangeAccountId
          : undefined;

      const rawStrategyId =
        typeof strategy === "object" && strategy !== null && "id" in strategy ? strategy.id : undefined;
      const strategyId =
        typeof rawStrategyId === "number" || typeof rawStrategyId === "string" ? rawStrategyId : undefined;

      if (!exchangeAccountId || !strategyId) {
        throw new Error("Exchange account and strategy are required for new bot");
      }

      const paymentMethodId = options?.paymentMethodId;

      // Parse userConfig if provided
      const parsedUserConfig = userConfig ? UserConfigSchema.parse(userConfig) : undefined;

      const createBody: BotConfigCreateInput = {
        exchange_account: exchangeAccountId,
        strategy: strategyId,
        userConfig: parsedUserConfig,
        subscription_paymentMethod: paymentMethodId,
      };

      const { data } = await botConfigsEndpoints.createBotConfig(createBody);
      notify.success("Bot creation finished correctly");
      return data;
    } else {
      const parsedUserConfig = userConfig ? UserConfigSchema.parse(userConfig) : undefined;
      const rawStrategyId =
        typeof strategy === "object" && strategy !== null && "id" in strategy ? strategy.id : undefined;
      const strategyId =
        typeof rawStrategyId === "number" || typeof rawStrategyId === "string" ? rawStrategyId : undefined;
      const updateBody: BotConfigUpdateInput = {
        disabled: disabled ?? undefined,
        userConfig: parsedUserConfig,
        strategy: strategyId,
      };

      const numericId = typeof id === "number" ? id : parseInt(String(id), 10);
      const { data } = await botConfigsEndpoints.updateBotConfig(numericId, updateBody);
      notify.success("Bot update finished correctly");
      return data;
    }
  } catch (e: unknown) {
    handleError(getErrorMessage(e, errMsg));
  }
};

export default useBot;
